import { Component, Input, Output, EventEmitter, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TemplateService } from '../../../core/services/template.service';
import { AlertService } from '../../../core/services/alert.service';
import { AnyBlock } from '../../../core/models/article-block.interface';
import { ArticleBuilderStructure } from '../../../core/models/blog.interface';
import { CreateTemplateDto, TemplateType } from '../../../core/models/template.interface';

@Component({
  selector: 'app-template-save-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './template-save-modal.component.html',
  styleUrls: ['./template-save-modal.component.scss']
})
export class TemplateSaveModalComponent implements OnInit {
  @Input() visible = false;
  @Input() blocks: AnyBlock[] = [];
  @Output() saved = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  private fb = inject(FormBuilder);
  private templateService = inject(TemplateService);
  private alertService = inject(AlertService);

  templateForm!: FormGroup;
  loading = signal(false);

  ngOnInit(): void {
    this.initForm();
  }

  private initForm(): void {
    this.templateForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(100)]],
      description: ['', [Validators.maxLength(500)]],
      type: [TemplateType.STRUCTURE_ONLY, [Validators.required]]
    });
  }

  onSave(): void {
    if (this.templateForm.invalid) {
      this.templateForm.markAllAsTouched();
      return;
    }

    if (!this.blocks || this.blocks.length === 0) {
      this.alertService.error('Nessun blocco da salvare nel template');
      return;
    }

    this.loading.set(true);
    const formValue = this.templateForm.getRawValue();

    // Converte i blocchi in ArticleBuilderStructure
    const builderData: ArticleBuilderStructure = this.mapBlocksToBuilderStructure(this.blocks, formValue.type);

    const createTemplateDto: CreateTemplateDto = {
      name: formValue.name,
      description: formValue.description || undefined,
      type: formValue.type,
      builderData
    };

    this.templateService.createTemplate(createTemplateDto).subscribe({
      next: () => {
        this.alertService.success('Template salvato con successo!');
        this.loading.set(false);
        this.resetForm();
        this.saved.emit();
        this.onCancel();
      },
      error: (error) => {
        console.error('Errore nel salvataggio del template:', error);
        this.alertService.error(error.error?.message || 'Errore nel salvataggio del template');
        this.loading.set(false);
      }
    });
  }

  onCancel(): void {
    this.resetForm();
    this.closed.emit();
  }

  private resetForm(): void {
    this.templateForm.reset({
      type: TemplateType.STRUCTURE_ONLY
    });
    this.loading.set(false);
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.templateForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getFieldError(fieldName: string): string {
    const field = this.templateForm.get(fieldName);
    if (field?.errors) {
      if (field.errors['required']) {
        return 'Campo obbligatorio';
      }
      if (field.errors['minlength']) {
        return `Minimo ${field.errors['minlength'].requiredLength} caratteri`;
      }
      if (field.errors['maxlength']) {
        return `Massimo ${field.errors['maxlength'].requiredLength} caratteri`;
      }
    }
    return '';
  }

  private mapBlocksToBuilderStructure(blocks: AnyBlock[], templateType: TemplateType): ArticleBuilderStructure {
    const processedBlocks = templateType === TemplateType.STRUCTURE_ONLY 
      ? this.stripContentFromBlocks(blocks)
      : blocks;

    return {
      version: '1.0.0',
      sections: [
        {
          id: 'section-main',
          name: 'Contenuto principale',
          settings: {},
          components: processedBlocks.map((block, index) => this.mapBlockToComponent(block, index)),
        },
      ],
    };
  }

  private stripContentFromBlocks(blocks: AnyBlock[]): AnyBlock[] {
    return blocks.map(block => {
      const strippedBlock = { ...block };
      
      switch (block.type) {
        case 'text':
          (strippedBlock as any).content = '';
          break;
        case 'heading':
          (strippedBlock as any).text = '';
          break;
        case 'image':
          (strippedBlock as any).url = '';
          (strippedBlock as any).alt = '';
          (strippedBlock as any).caption = '';
          break;
        case 'quote':
          (strippedBlock as any).text = '';
          (strippedBlock as any).author = '';
          break;
        // spacer e divider mantengono le loro proprietà
      }
      
      return strippedBlock;
    });
  }

  private mapBlockToComponent(block: AnyBlock, order: number): any {
    const base = {
      id: block.id,
      type: block.type,
      content: {},
      style: {},
      meta: { order },
    };

    switch (block.type) {
      case 'text':
        base.content = { html: (block as any).content ?? '' };
        break;
      case 'heading':
        base.content = {
          level: (block as any).level ?? 2,
          text: (block as any).text ?? '',
        };
        break;
      case 'image':
        base.content = {
          url: (block as any).url ?? '',
          alt: (block as any).alt ?? '',
          caption: (block as any).caption ?? '',
          width: (block as any).width ?? 'full',
        };
        break;
      case 'quote':
        base.content = {
          text: (block as any).text ?? '',
          author: (block as any).author ?? '',
        };
        break;
      case 'spacer':
        base.content = { height: (block as any).height ?? 40 };
        break;
      case 'divider':
        base.content = {
          marginY: (block as any).marginY ?? 'my-5',
        };
        break;
      default:
        base.content = {};
    }

    return base;
  }
}








